// Direktkunden-Landing für Solar- und Wärmepumpen-Betriebe.
// Gleiches Layout/Animations-System wie AgencyLanding, aber inhaltlich auf
// den Endkunden-Betrieb zugeschnitten (Direktkundenpreise 149/299/599).

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sun, Thermometer, Zap, Flame, TrendingUp, Users, Sparkles,
  Check, X, ArrowRight, ChevronDown, Wallet, Inbox, Mail,
  Briefcase, MessageSquare, PhoneCall, Star, Quote, Calendar, Gauge,
} from "lucide-react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  SectionHeader, Counter, NoiseOverlay, MockChrome,
  MockConversionQueue, MockConfigurator, MockPipeline,
  MockAIChat, MockReporting, MockProductReporting, fadeUp,
} from "@/pages/AgencyLanding";

// ============= ROOT =============
export default function Landing() {
  return (
    <div className="min-h-screen bg-[#fafaf9] text-slate-900 selection:bg-orange-200" data-testid="landing-v2" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <NavBar />
      <Hero />
      <LogoStrip />
      <Comparison />
      <FeatureShowcase />
      <MultiProduct />
      <RoiCalculator />
      <Pricing />
      <Testimonials />
      <HowItWorks />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ============= NAV =============
function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white shadow-md group-hover:rotate-12 transition-transform">
            <Sun className="w-5 h-5" />
          </div>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>revenioo<span className="text-orange-600">.ai</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
          <a href="#features" className="hover:text-slate-900 transition-colors">Funktionen</a>
          <a href="#multi-produkt" className="hover:text-slate-900 transition-colors">PV & Wärmepumpe</a>
          <a href="#roi" className="hover:text-slate-900 transition-colors">ROI</a>
          <a href="#pricing" className="hover:text-slate-900 transition-colors">Preise</a>
          <a href="#faq" className="hover:text-slate-900 transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/agentur" data-testid="nav-agency" className="hidden md:inline-block text-sm font-medium text-slate-500 hover:text-slate-700 px-3 py-2">Für Agenturen</Link>
          <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2">Login</Link>
          <Link to="/login" data-testid="nav-cta" className="text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-lg shadow-[0_8px_24px_-8px_rgba(234,88,12,0.6)] hover:-translate-y-0.5 transition-all">Kostenlos starten</Link>
        </div>
      </div>
    </header>
  );
}

// ============= HERO =============
function Hero() {
  return (
    <section className="relative pt-12 md:pt-20 pb-20 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-radial from-orange-200/40 via-transparent to-transparent blur-3xl" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-sky-200/30 rounded-full blur-3xl" />
        <NoiseOverlay />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }} className="lg:col-span-6">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-semibold text-slate-700 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Für Solar- und Wärmepumpen-Betriebe
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Aus jeder <span className="relative inline-block">
              <span className="relative z-10">Anfrage</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-orange-300/60 -z-0 rounded-sm" />
            </span><br />
            ein <span className="text-orange-600">Auftrag</span>.
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
            Vertriebs-Plattform für <strong className="text-slate-900">Photovoltaik</strong> und <strong className="text-slate-900">Wärmepumpe</strong>:
            Konfigurator, Hot-Lead-Erkennung, automatische Preisschätzung und Pipeline — eine Lösung
            für beide Produkte, ohne Excel und Tool-Chaos.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
            <Link to="/login" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 rounded-xl font-bold text-base shadow-[0_0_40px_-10px_rgba(234,88,12,0.7)] hover:shadow-[0_0_60px_-12px_rgba(234,88,12,0.8)] hover:-translate-y-1 transition-all">
              Kostenlos starten <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-800 px-7 py-4 rounded-xl font-bold text-base transition-all hover:bg-slate-50">
              Funktionen ansehen
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <div className="flex -space-x-2">
              {["#fdba74", "#a78bfa", "#22d3ee", "#34d399"].map((c, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: c }} />
              ))}
            </div>
            <div>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-xs mt-0.5">DSGVO-konform · Server in der EU · Stripe-abgesichert</p>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  const floats = [
    { Icon: Flame, text: "Sofort-Lead Markus H.", color: "from-orange-500 to-rose-500", style: "top-4 -left-6", delay: 0.5 },
    { Icon: Wallet, text: "+ 15.100 € gewonnen", color: "from-emerald-500 to-teal-500", style: "bottom-8 -left-8", delay: 0.8 },
    { Icon: Thermometer, text: "Wärmepumpe ✓", color: "from-sky-500 to-blue-500", style: "-top-4 right-8", delay: 1.1 },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
      <div className="absolute -inset-8 bg-gradient-to-tr from-orange-400/30 via-sky-400/20 to-transparent blur-3xl -z-10 rounded-[3rem]" />
      <MockMandantDashboard tilt />
      {floats.map((f, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.7, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: f.delay, duration: 0.5 }}
          className={`absolute ${f.style} hidden md:block`}
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
            className={`bg-gradient-to-r ${f.color} text-white px-3 py-2 rounded-xl shadow-2xl flex items-center gap-2 backdrop-blur`}
          >
            <f.Icon className="w-4 h-4" />
            <span className="text-xs font-bold whitespace-nowrap">{f.text}</span>
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
}

// Eigener Mockup für den Solar-/WP-Betrieb (zeigt Pipeline statt Agentur-Dashboard)
function MockMandantDashboard({ tilt = false }) {
  const kpis = [
    { l: "Hot Leads", v: "4", c: "text-orange-600" },
    { l: "Pipeline-Wert", v: "189.560 €", c: "text-slate-900" },
    { l: "Antwortquote", v: "66,7 %", c: "text-emerald-600" },
    { l: "Gewonnen", v: "15.100 €", c: "text-emerald-600" },
  ];
  const stages = [
    { l: "Anfrage", n: 4, color: "bg-blue-100 text-blue-800" },
    { l: "Angebot", n: 2, color: "bg-amber-100 text-amber-800" },
    { l: "Nachfass", n: 4, color: "bg-orange-100 text-orange-800" },
    { l: "Auftrag", n: 1, color: "bg-emerald-100 text-emerald-800" },
  ];
  return (
    <div className={tilt ? "transform-gpu hover:rotate-0 transition-transform duration-700" : ""} style={tilt ? { transform: "perspective(1500px) rotateY(-6deg) rotateX(2deg)" } : {}}>
      <MockChrome label="meinbetrieb.revenioo.ai">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">Vertrieb · Diesen Monat</p>
              <h4 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>SolarFox GmbH</h4>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Live</span>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {kpis.map((k, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.06 }}
                className="p-2.5 rounded-lg bg-slate-50 border border-slate-100"
              >
                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{k.l}</p>
                <p className={`text-sm font-extrabold tabular-nums ${k.c}`} style={{ fontFamily: "Manrope, sans-serif" }}>{k.v}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Pipeline</p>
          <div className="grid grid-cols-4 gap-1.5">
            {stages.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + i * 0.08 }}
                className="p-2 rounded-lg bg-white border border-slate-200 text-center"
              >
                <span className={`inline-block px-1.5 py-0.5 rounded ${s.color} text-[8px] font-bold uppercase mb-1`}>{s.l}</span>
                <p className="text-base font-extrabold tabular-nums text-slate-900">{s.n}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </MockChrome>
    </div>
  );
}

// ============= LOGO STRIP =============
function LogoStrip() {
  const logos = ["SolarFox", "HelioPower", "WärmeWelt", "PV-Direkt", "ÖkoTherm", "Heliosa"];
  return (
    <section className="py-12 border-y border-slate-200/70 bg-white/40">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">Genutzt von Solar- und Heizungsbetrieben in DACH</p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
          {logos.map((l, i) => (
            <motion.div key={l} initial={{ opacity: 0 }} whileInView={{ opacity: 0.6 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="text-slate-400 font-bold text-xl tracking-tight hover:opacity-100 hover:text-slate-700 transition-all" style={{ fontFamily: "Manrope, sans-serif" }}>
              {l}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= COMPARISON =============
function Comparison() {
  const without = [
    "Anfragen versanden im E-Mail-Postfach",
    "Excel-Liste fürs Nachfassen — wer ist dran?",
    "Kunden warten 5 Tage auf eine Preisspanne",
    "Wärmepumpen-Anfragen separat im Heizungs-CRM",
    'Vertrieb fragt: „Welcher Kunde war wichtig?"',
    "Geretteter Umsatz? Nur Bauchgefühl, keine Zahlen",
  ];
  const withUs = [
    "Hot-Lead-Erkennung priorisiert automatisch",
    "Conversion Queue zeigt: wer braucht heute Kontakt",
    "Konfigurator liefert Preisschätzung in 60 Sekunden",
    "PV, WP & Kombi in einem Konfigurator, eine Pipeline",
    "Pipeline-Wert, Hot-Leads, Recovery — live im Dashboard",
    "Reporting zeigt: geretteter Umsatz, reaktivierter Wert",
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Warum revenioo.ai"
          title="Ihr Vertrieb — vorher vs. nachher"
          sub="Sie holen mehr Aufträge aus den gleichen Anfragen. Ihre Kunden merken den Unterschied schon beim ersten Kontakt."
        />
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-14">
          <CompareCol icon={X} title="Ohne revenioo.ai" items={without} negative />
          <CompareCol icon={Check} title="Mit revenioo.ai" items={withUs} />
        </div>
      </div>
    </section>
  );
}
function CompareCol({ icon: Icon, title, items, negative = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      className={`p-7 md:p-9 rounded-2xl ${negative ? "bg-slate-100 border border-slate-200" : "bg-gradient-to-br from-slate-900 to-slate-800 text-white border border-slate-700 shadow-2xl"}`}
    >
      <div className="flex items-center gap-3 mb-7">
        <div className={`w-11 h-11 rounded-xl grid place-items-center ${negative ? "bg-slate-200 text-slate-500" : "bg-orange-500/20 text-orange-400"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 * i, duration: 0.4 }}
            className="flex items-start gap-3"
          >
            <span className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full grid place-items-center ${negative ? "bg-rose-100 text-rose-600" : "bg-emerald-500/20 text-emerald-400"}`}>
              {negative ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            </span>
            <span className={`text-sm md:text-[15px] ${negative ? "text-slate-700" : "text-slate-200"}`}>{it}</span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
}

// ============= FEATURE SHOWCASE =============
const FEATURES = [
  { key: "configurator", label: "Multi-Produkt-Konfigurator", icon: Zap, desc: "Ein Konfigurator für Solar, Wärmepumpe und Kombi. Liefert Ihren Kunden eine unverbindliche Preisschätzung inkl. Förderspanne — direkt nach dem Absenden.", render: () => <MockConfigurator /> },
  { key: "pipeline", label: "Vertriebs-Pipeline", icon: Briefcase, desc: "Kanban-Board: Anfrage → Angebot → Nachfass → Auftrag. Pipeline-Wert immer im Blick. Drag-and-Drop für Status-Wechsel.", render: () => <MockPipeline /> },
  { key: "hotleads", label: "Hot-Lead-Erkennung", icon: Flame, desc: 'Conversion Queue priorisiert Sofort-Leads automatisch. Schluss mit „wer war nochmal wichtig?" — die Liste sortiert sich selbst.', render: () => <MockConversionQueue /> },
  { key: "ai", label: "KI für Vertrieb", icon: Sparkles, desc: "Reaktivierungs-Texte für kalte Leads, Einwand-Coach für schwierige Gespräche, Follow-up-Vorschläge — KI hilft Ihrem Vertrieb, mehr Pipeline in Umsatz zu drehen.", render: () => <MockAIChat /> },
  { key: "reporting", label: "Reporting & Recovery", icon: TrendingUp, desc: "Geretteter Umsatz, reaktivierter Wert, Antwortquote, Hot-Lead-KPIs — aufgeschlüsselt nach Solar, Wärmepumpe und Kombi.", render: () => <MockReporting /> },
];

function FeatureShowcase() {
  const [active, setActive] = useState(FEATURES[0].key);
  const current = FEATURES.find((f) => f.key === active) || FEATURES[0];
  return (
    <section id="features" className="py-24 md:py-32 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Alles in einer Plattform"
          title="Fünf Funktionen, ein Workflow"
          sub="Sticky-Vorschau rechts — Tab links anklicken zum Wechseln."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-2">
            {FEATURES.map((f) => {
              const isActive = f.key === active;
              return (
                <button
                  key={f.key}
                  onClick={() => setActive(f.key)}
                  data-testid={`feature-tab-${f.key}`}
                  className={`w-full text-left p-5 rounded-xl border-l-4 transition-all ${
                    isActive
                      ? "border-orange-500 bg-orange-50/60 shadow-sm"
                      : "border-transparent hover:border-slate-200 hover:bg-slate-50 opacity-70 hover:opacity-100"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-1.5">
                    <f.icon className={`w-5 h-5 ${isActive ? "text-orange-600" : "text-slate-400"}`} />
                    <span className={`font-bold text-base ${isActive ? "text-slate-900" : "text-slate-700"}`} style={{ fontFamily: "Manrope, sans-serif" }}>{f.label}</span>
                  </div>
                  <AnimatePresence>
                    {isActive && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="text-sm text-slate-600 leading-relaxed overflow-hidden"
                      >
                        {f.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-24">
              <div className="relative rounded-2xl bg-gradient-to-br from-slate-50 to-white p-3 border border-slate-200 shadow-2xl shadow-slate-300/40">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, scale: 0.97, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97, y: -8 }}
                    transition={{ duration: 0.35 }}
                  >
                    {current.render()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============= MULTI PRODUKT =============
function MultiProduct() {
  const items = [
    { Icon: Sun, color: "from-amber-400 to-orange-500", title: "Photovoltaik", lead: "Klassiker mit Förderung", desc: "PV-Funnel mit Schritt-für-Schritt-Konfigurator und Preisschätzung inkl. KfW- und Landesförderung." },
    { Icon: Thermometer, color: "from-sky-500 to-blue-600", title: "Wärmepumpe", lead: "BAFA-Förderung integriert", desc: "Eigener WP-Pfad: Heizungstyp, Wohnfläche, Aufstellort. Schätzung mit BAFA-Spanne und Heizlast-Hinweis." },
    { Icon: Zap, color: "from-violet-500 to-fuchsia-600", title: "PV + Wärmepumpe", lead: "Höchste Auftragswerte", desc: "Kombi-Pfad mit Komponenten-Breakdown und Gesamt-Estimate. Für den maximalen Hebel beim Kunden." },
  ];
  return (
    <section id="multi-produkt" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-orange-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-sky-200/20 rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Nicht nur PV. Auch Wärmepumpe."
          title="Ein Konfigurator, drei Produktpfade"
          sub="Sie verkaufen beides? Sie brauchen kein zweites Tool. Solar, Wärmepumpe und Kombi laufen in derselben Pipeline — mit jeweils eigener Förder-Logik."
        />
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              whileHover={{ y: -6 }}
              className="relative p-7 rounded-2xl bg-white border border-slate-200 shadow-lg hover:shadow-2xl transition-shadow group overflow-hidden"
            >
              <div className={`absolute -top-12 -right-12 w-40 h-40 bg-gradient-to-br ${it.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${it.color} grid place-items-center text-white shadow-lg mb-5`}>
                <it.Icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>{it.title}</h3>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{it.lead}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-5 shadow-xl"
        >
          <MockProductReporting />
        </motion.div>
      </div>
    </section>
  );
}

// ============= ROI-CALCULATOR =============
function RoiCalculator() {
  const [leads, setLeads] = useState(80);  // Anfragen / Monat
  // Branchenrealistische Annahmen:
  // - Hot-Lead-Quote 25 %
  // - Conversion Hot → Auftrag 35 %
  // - Ø Auftragswert PV / WP ca. 22.000 €
  const HOT_RATE = 0.25;
  const CONV = 0.35;
  const AVG_DEAL = 22000;
  const hot = Math.round(leads * HOT_RATE);
  const orders = Math.round(hot * CONV);
  const pipeline = orders * AVG_DEAL;
  return (
    <section id="roi" className="py-24 md:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
      <NoiseOverlay />
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-500/15 rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <SectionHeader
          dark
          eyebrow="ROI-Rechner"
          title="Aus Ihren Anfragen werden Aufträge."
          sub="Realistische Branchenwerte: 25 % Hot-Lead-Quote · 35 % Auftragsquote auf Hot Leads · Ø 22.000 € Auftragswert (PV/WP gemischt)."
        />
        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-300 font-bold mb-3">Anfragen pro Monat</p>
            <div className="flex items-baseline gap-3 mb-6">
              <Counter value={leads} className="text-7xl font-extrabold tracking-tighter text-white" style={{ fontFamily: "Manrope, sans-serif" }} />
              <span className="text-slate-400 text-lg">Leads</span>
            </div>
            <input
              type="range"
              min="20"
              max="300"
              step="10"
              value={leads}
              onChange={(e) => setLeads(parseInt(e.target.value))}
              data-testid="roi-slider"
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>20</span><span>150</span><span>300</span>
            </div>
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">
              Mit Hot-Lead-Erkennung und Conversion Queue holen Sie aus jedem Lead mehr raus — schon ohne mehr Marketing-Budget.
            </p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-3 gap-3">
            <RoiTile Icon={Flame} label="Hot Leads / Monat" value={hot} sub={`${Math.round(HOT_RATE * 100)} % von ${leads}`} accent="text-orange-400" />
            <RoiTile Icon={Check} label="Aufträge / Monat" value={orders} sub={`${Math.round(CONV * 100)} % der Hot Leads`} accent="text-white" />
            <RoiTile Icon={TrendingUp} label="Pipeline / Monat" value={pipeline} sub="Ø 22.000 € / Auftrag" accent="text-orange-400" highlight money />
          </div>
        </div>
        <div className="mt-12 max-w-2xl mx-auto text-center">
          <p className="text-slate-300 text-sm">
            Bei <strong className="text-white">{leads} Anfragen / Monat</strong> generieren Sie
            <strong className="text-orange-400 mx-1.5">{pipeline.toLocaleString("de-DE")} €</strong>
            Pipeline-Wert — pro Jahr <strong className="text-orange-400">{(pipeline * 12).toLocaleString("de-DE")} €</strong>.
          </p>
        </div>
      </div>
    </section>
  );
}

function RoiTile({ Icon, label, value, sub, accent, highlight = false, money = false }) {
  return (
    <div className={`p-5 rounded-2xl ${highlight ? "bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/40" : "bg-white/5 border border-white/10"} backdrop-blur`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-3.5 h-3.5 ${accent}`} />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</p>
      </div>
      <div className="flex items-baseline gap-1">
        <Counter value={value} className={`text-3xl md:text-4xl font-extrabold tracking-tighter tabular-nums ${accent}`} style={{ fontFamily: "Manrope, sans-serif" }} />
        {money && <span className={`text-lg ${accent}`}>€</span>}
      </div>
      <p className="text-xs text-slate-500 mt-1.5">{sub}</p>
    </div>
  );
}

// ============= PRICING =============
const PLANS = [
  { name: "Starter", monthly: 149, yearly: 1490, leads: "50", desc: "Für Solar- oder Heizungsbetriebe, die mit System anfangen." },
  { name: "Growth", monthly: 299, yearly: 2990, leads: "250", desc: "Der Standard — passt für die meisten Betriebe mit aktivem Vertrieb.", popular: true },
  { name: "Scale", monthly: 599, yearly: 5990, leads: "1.000", desc: "Für Betriebe mit hohem Anfragevolumen oder mehreren Standorten." },
];

function Pricing() {
  const [yearly, setYearly] = useState(false);
  return (
    <section id="pricing" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Faire Preise"
          title="Drei Pakete, die mit Ihrem Vertrieb wachsen"
          sub="Pro Betrieb · Jahreszahlung: 2 Monate geschenkt · Kein Setup-Fee, kein Trial-Kleingedrucktes"
        />
        <div className="mt-10 flex items-center justify-center gap-3" data-testid="pricing-toggle">
          <button
            data-testid="pricing-toggle-monthly"
            onClick={() => setYearly(false)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${!yearly ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-700"}`}
          >
            Monatlich
          </button>
          <button
            data-testid="pricing-toggle-yearly"
            onClick={() => setYearly(true)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${yearly ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-700"}`}
          >
            Jährlich
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid="pricing-yearly-savings-badge">
              2 Monate geschenkt
            </span>
          </button>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PLANS.map((p, i) => {
            const eff = yearly ? p.yearly / 12 : p.monthly;
            const effStr = yearly ? eff.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : eff.toLocaleString("de-DE");
            const slug = p.name.toLowerCase();
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                data-testid={`plan-${slug}`}
                className={`relative p-7 rounded-2xl bg-white border ${p.popular ? "border-orange-500 shadow-2xl shadow-orange-500/20 ring-2 ring-orange-500/20" : "border-slate-200 shadow-lg"}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md">Beliebt</span>
                )}
                <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{p.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-slate-900 tracking-tighter tabular-nums" style={{ fontFamily: "Manrope, sans-serif" }} data-testid={`plan-amount-${slug}`}>{effStr}</span>
                  <span className="text-slate-500 text-base ml-1">€ / Monat</span>
                </div>
                <p className="text-xs text-slate-500 mt-2 min-h-[16px]" data-testid={`plan-billing-note-${slug}`}>
                  {yearly ? `${p.yearly.toLocaleString("de-DE")} € pro Jahr · 2 Monate geschenkt` : "monatliche Abrechnung"}
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {p.leads} verarbeitete Leads / Monat</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Alle Funktionen ohne Limits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Solar, Wärmepumpe & Kombi</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Hot-Lead-Recovery & KI-Chat</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Eigene Subdomain & Branding</li>
                </ul>
                <Link to="/login" data-testid={`plan-cta-${slug}`} className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${p.popular ? "bg-orange-600 hover:bg-orange-700 text-white shadow-md hover:shadow-lg" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  Jetzt starten <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            );
          })}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 max-w-2xl mx-auto text-center"
        >
          <p className="text-sm text-slate-500">
            Sind Sie eine Agentur und möchten mehrere Mandanten betreuen? ·
            <Link to="/agentur" className="text-orange-600 hover:text-orange-700 font-semibold ml-1">Zur Wholesale-Lösung</Link>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ============= TESTIMONIALS =============
function Testimonials() {
  const t = [
    { name: "Stefan Müller", role: "Geschäftsführer · SolarFox GmbH", text: "Wir holen aus den gleichen Anfragen 40 % mehr Aufträge. Die Hot-Lead-Liste sortiert sich morgens schon selbst." },
    { name: "Petra Wagner", role: "Vertriebsleiterin · WärmeWelt Heiztechnik", text: "Endlich ein Tool, das Wärmepumpen-Förderung versteht. Unsere Kunden bekommen die Preisspanne sofort — nicht erst nach 5 Tagen." },
    { name: "Markus Heinrich", role: "Inhaber · HelioPower", text: "Die Kombi PV+WP läuft jetzt im selben Funnel. Höhere Aufträge, schnellere Abschlüsse, weniger Excel-Chaos." },
  ];
  return (
    <section className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Testimonials" title="Stimmen aus dem Energie-Vertrieb" sub="Was Solar- und Heizungsbetriebe sagen, die mit revenioo.ai mehr aus ihrer Pipeline holen." />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {t.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-7 bg-white rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-shadow relative"
            >
              <Quote className="absolute top-5 right-5 w-7 h-7 text-orange-200" />
              <div className="flex gap-0.5 mb-4">{[1,2,3,4,5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
              <p className="text-[15px] text-slate-700 leading-relaxed">&ldquo;{it.text}&rdquo;</p>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-sky-500 grid place-items-center text-white font-bold text-sm">
                  {it.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">{it.name}</p>
                  <p className="text-xs text-slate-500">{it.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= HOW IT WORKS =============
function HowItWorks() {
  const steps = [
    { n: "01", title: "Account in 60 Sekunden", desc: "Registrieren, Logo + Farbe einstellen, fertig. Keine Setup-Gebühr, kein Trial-Kleingedrucktes." },
    { n: "02", title: "Konfigurator auf Ihrer Webseite", desc: "Iframe oder Snippet einbetten — PV, WP oder Kombi. Ihre Kunden bekommen sofort eine Preisschätzung." },
    { n: "03", title: "Aufträge in der Pipeline", desc: "Hot-Lead-Erkennung, automatische Preisschätzung, Pipeline-Eintrag. Sie kontaktieren nur die priorisierten Anfragen." },
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="In 3 Schritten" title="So funktioniert es" />
        <div className="mt-14 grid md:grid-cols-3 gap-5 relative">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent" />
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="relative bg-white rounded-2xl border border-slate-200 p-7 shadow-md"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white font-extrabold text-lg shadow-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                {s.n}
              </div>
              <h3 className="mt-5 text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= FAQ =============
function FAQ() {
  const items = [
    { q: "Funktioniert das auch, wenn ich nur Wärmepumpen verkaufe?", a: "Ja. Der Konfigurator hat einen vollständig eigenständigen WP-Pfad mit Heizungstyp, Wohnfläche, Aufstellort und einer Preisschätzung inklusive BAFA-Förderspanne. PV ist optional ausblendbar." },
    { q: "Was zählt als verarbeiteter Lead?", a: "Jeder Lead, der aus dem Konfigurator, manueller Anlage, Bedarfsanalyse oder KI-Chat entsteht. Sobald das monatliche Limit erreicht ist, empfehlen wir ein Upgrade — keine Sperre durch Hintertür." },
    { q: "Wie schnell ist die Plattform einsatzbereit?", a: "Account anlegen → Logo & Farbe → Konfigurator-Snippet kopieren. In unter 60 Sekunden ist die erste Anfrage testbar." },
    { q: "Brauche ich technisches Know-how für die Integration?", a: "Nein. Das Konfigurator-Snippet ist ein einfacher Iframe-Embed-Code. Jeder Webmaster bekommt das in 5 Minuten unter — wir helfen auf Wunsch direkt." },
    { q: "Was passiert mit meinen Lead-Daten?", a: "DSGVO-konforme EU-Server in Frankfurt. Sie können Ihre Daten jederzeit exportieren. Kein US-Cloud-Drittland-Transfer." },
    { q: "Kann ich vom Starter auf Growth upgraden?", a: "Jederzeit — anteilig abgerechnet. Auch Downgrades sind möglich, wenn das monatliche Lead-Volumen das erlaubt." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 bg-white border-y border-slate-200">
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="FAQ" title="Häufige Fragen" />
        <div className="mt-12 space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-q-${i}`}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900 text-base">{it.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{it.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============= FINAL CTA =============
function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white relative overflow-hidden">
      <NoiseOverlay />
      <div className="absolute inset-0 -z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-orange-500/30 via-transparent to-transparent blur-3xl" />
      </div>
      <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter" style={{ fontFamily: "Manrope, sans-serif" }}>
          Mehr <span className="text-orange-400">Aufträge</span> aus den Anfragen, die Sie schon haben.
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
          Solar, Wärmepumpe und Kombi in einer Plattform. Hot-Lead-Erkennung, Konfigurator, Pipeline und KI-Chat — sofort einsatzbereit.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/login" data-testid="final-cta-primary" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-xl font-bold text-base shadow-[0_0_60px_-10px_rgba(234,88,12,0.8)] hover:-translate-y-1 transition-all">
            Kostenlos starten <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" data-testid="final-cta-secondary" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/15 px-8 py-4 rounded-xl font-bold text-base transition-all">
            Demo buchen
          </Link>
        </motion.div>
        <p className="mt-6 text-xs text-slate-400">DSGVO-konforme EU-Server · Stripe-abgesichert · Kein Setup-Fee</p>
      </div>
    </section>
  );
}

// ============= FOOTER =============
function Footer() {
  return (
    <footer className="py-10 bg-slate-950 text-slate-400 text-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white"><Sun className="w-4 h-4" /></div>
          <span className="text-white font-bold">revenioo.ai</span>
          <span className="text-slate-500">· © {new Date().getFullYear()}</span>
        </div>
        <div className="flex gap-5">
          <Link to="/agentur" className="hover:text-white">Für Agenturen</Link>
          <Link to="/impressum" className="hover:text-white">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-white">Datenschutz</Link>
          <Link to="/login" className="hover:text-white">Login</Link>
        </div>
      </div>
    </footer>
  );
}
